import React from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import { Box } from '@enechain/ecloud-designsystem'
import {
  StatusBadge,
  getTextAlignLightTableStyle,
} from '@enechain/eposition-ui'
import { stringUtils } from '@enechain/eposition-utils'

import { AvailableDealBaseTable } from '~/pages/Simulation/components/SimulationAndResultArea/AvailableDealSimulationSection/ResultSection/AvailableDealBaseTable'
import { ResultAvailableDealSimulationForOption2Models } from '~/pages/Simulation/models/SimulationResultModel/ResultAvailableDealSimulationDetailForOption2Models'
import { ResultSimulationForOption2Model } from '~/pages/Simulation/models/SimulationResultModel/ResultSimulationForOption2Model'

const MAX_COLUMN_WIDTH = 106
const RELATION_COLUMN_WIDTH = 72
const DEAL_STATUS_COLUMN_WIDTH = 60

type Props = {
  resultDetail: ResultAvailableDealSimulationForOption2Models | undefined
}

export const ResultAvailableDealSimulationForOption2Table: React.FC<Props> = ({
  resultDetail,
}) => {
  const { t } = useTranslation(['page', 'common', 'domain'])
  const columnHelper = createColumnHelper<ResultSimulationForOption2Model>()

  const columns = [
    columnHelper.accessor('orderRelationString', {
      header: t('page:simulation.order_relation_number'),
      cell: (info) => <Box>{info.getValue()}</Box>,
      enableSorting: false,
      size: RELATION_COLUMN_WIDTH,
      meta: {
        headerStyle: {
          minWidth: stringUtils.convertPxFromNumber(RELATION_COLUMN_WIDTH),
          maxWidth: stringUtils.convertPxFromNumber(RELATION_COLUMN_WIDTH),
          width: stringUtils.convertPxFromNumber(RELATION_COLUMN_WIDTH),
          whiteSpace: 'pre-wrap',
        },
        cellStyle: {
          width: stringUtils.convertPxFromNumber(RELATION_COLUMN_WIDTH),
          minWidth: stringUtils.convertPxFromNumber(RELATION_COLUMN_WIDTH),
          maxWidth: stringUtils.convertPxFromNumber(RELATION_COLUMN_WIDTH),
        },
      },
    }),
    columnHelper.accessor('allowDealStatus', {
      header: t('page:simulation.deal_availability'),
      cell: (info) => {
        const statusValue = info.getValue()
        return <StatusBadge value={statusValue} />
      },
      enableSorting: false,
      size: DEAL_STATUS_COLUMN_WIDTH,
      meta: {
        headerStyle: {
          minWidth: stringUtils.convertPxFromNumber(DEAL_STATUS_COLUMN_WIDTH),
          maxWidth: stringUtils.convertPxFromNumber(DEAL_STATUS_COLUMN_WIDTH),
          width: stringUtils.convertPxFromNumber(DEAL_STATUS_COLUMN_WIDTH),
          whiteSpace: 'pre-wrap',
          borderRight: '1px solid',
          borderColor: 'Border.midEmphasis',
        },
        cellStyle: {
          minWidth: stringUtils.convertPxFromNumber(DEAL_STATUS_COLUMN_WIDTH),
          maxWidth: stringUtils.convertPxFromNumber(DEAL_STATUS_COLUMN_WIDTH),
          width: stringUtils.convertPxFromNumber(DEAL_STATUS_COLUMN_WIDTH),
          borderRight: '1px solid',
          borderColor: 'Border.midEmphasis',
        },
      },
    }),
    columnHelper.accessor('allRiskAmount', {
      header: t('page:simulation.all_risk_amount'),
      cell: (info) => <Box>{info.getValue()}</Box>,
      enableSorting: false,
      minSize: MAX_COLUMN_WIDTH,
      meta: {
        ...getTextAlignLightTableStyle(),
        headerStyle: {
          ...getTextAlignLightTableStyle().headerStyle,
          minWidth: stringUtils.convertPxFromNumber(MAX_COLUMN_WIDTH),
          whiteSpace: 'pre-wrap',
        },
        cellStyle: {
          ...getTextAlignLightTableStyle().cellStyle,
          minWidth: stringUtils.convertPxFromNumber(MAX_COLUMN_WIDTH),
        },
      },
    }),
    columnHelper.accessor('allAvailableAmount', {
      header: t('page:simulation.all_available_amount'),
      cell: (info) => <Box>{info.getValue()}</Box>,
      enableSorting: false,
      minSize: MAX_COLUMN_WIDTH,
      meta: {
        ...getTextAlignLightTableStyle(),
        headerStyle: {
          ...getTextAlignLightTableStyle().headerStyle,
          minWidth: stringUtils.convertPxFromNumber(MAX_COLUMN_WIDTH),
          whiteSpace: 'pre-wrap',
        },
        cellStyle: {
          ...getTextAlignLightTableStyle().cellStyle,
          minWidth: stringUtils.convertPxFromNumber(MAX_COLUMN_WIDTH),
        },
      },
    }),
    columnHelper.accessor('physicalFuturesRiskAmount', {
      header: t('page:simulation.physical_futures_risk_amount'),
      cell: (info) => <Box>{info.getValue()}</Box>,
      enableSorting: false,
      minSize: MAX_COLUMN_WIDTH,
      meta: {
        ...getTextAlignLightTableStyle(),
        headerStyle: {
          ...getTextAlignLightTableStyle().headerStyle,
          minWidth: stringUtils.convertPxFromNumber(MAX_COLUMN_WIDTH),
          whiteSpace: 'pre-wrap',
        },
        cellStyle: {
          ...getTextAlignLightTableStyle().cellStyle,
          minWidth: stringUtils.convertPxFromNumber(MAX_COLUMN_WIDTH),
        },
      },
    }),
    columnHelper.accessor('physicalFuturesAvailableAmount', {
      header: t('page:simulation.physical_futures_available_amount'),
      cell: (info) => <Box>{info.getValue()}</Box>,
      enableSorting: false,
      minSize: MAX_COLUMN_WIDTH,
      meta: {
        ...getTextAlignLightTableStyle(),
        headerStyle: {
          ...getTextAlignLightTableStyle().headerStyle,
          minWidth: stringUtils.convertPxFromNumber(MAX_COLUMN_WIDTH),
          whiteSpace: 'pre-wrap',
        },
        cellStyle: {
          ...getTextAlignLightTableStyle().cellStyle,
          minWidth: stringUtils.convertPxFromNumber(MAX_COLUMN_WIDTH),
        },
      },
    }),
  ]

  if (resultDetail === undefined) {
    return null
  }

  return (
    <AvailableDealBaseTable data={resultDetail.details} columns={columns} />
  )
}
